import { createContext, use, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, api } from '@/api/client';
import type { WhoAmI } from '@/api/client';

interface AuthContextValue {
  user: WhoAmI | null;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  logoutAll: () => Promise<void>;
  loginError: string | null;
  isLoggingIn: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();

  const meQuery = useQuery<WhoAmI | null>({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      try {
        return await api.me();
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          return null;
        }
        throw err;
      }
    },
    staleTime: 60_000,
    retry: false,
  });

  const loginMut = useMutation({
    mutationFn: ({ username, password }: { username: string; password: string }) =>
      api.login(username, password),
    onSuccess: (data) => {
      qc.setQueryData(['auth', 'me'], data.user);
    },
  });

  const logoutMut = useMutation({
    mutationFn: api.logout,
    onSuccess: () => {
      qc.setQueryData(['auth', 'me'], null);
      qc.removeQueries({ queryKey: ['chart'] });
    },
  });

  const logoutAllMut = useMutation({
    mutationFn: api.logoutAll,
    onSuccess: () => {
      qc.setQueryData(['auth', 'me'], null);
      qc.removeQueries({ queryKey: ['chart'] });
    },
  });

  const login = useCallback(
    async (username: string, password: string) => {
      await loginMut.mutateAsync({ username, password });
    },
    [loginMut],
  );

  const logout = useCallback(async () => {
    await logoutMut.mutateAsync();
  }, [logoutMut]);

  const logoutAll = useCallback(async () => {
    await logoutAllMut.mutateAsync();
  }, [logoutAllMut]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: meQuery.data ?? null,
      isLoading: meQuery.isLoading,
      login,
      logout,
      logoutAll,
      isLoggingIn: loginMut.isPending,
      loginError: loginMut.error
        ? loginMut.error instanceof ApiError
          ? humaniseLoginError(loginMut.error)
          : 'Network error — please try again'
        : null,
    }),
    [meQuery.data, meQuery.isLoading, login, logout, logoutAll, loginMut.isPending, loginMut.error],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

function humaniseLoginError(err: ApiError): string {
  switch (err.detail) {
    case 'invalid_credentials':
      return 'Invalid username or password.';
    case 'too_many_attempts':
      return 'Too many failed attempts. Try again in 15 minutes.';
    case 'account_locked':
      return 'Account locked. Contact the professor.';
    default:
      return `Login failed (${err.status}).`;
  }
}

export function useAuth(): AuthContextValue {
  const ctx = use(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
